import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { intersectionBy, toUpper } from 'lodash'
import { isDeepEmpty } from '../../helpers/filter'
import React from 'react'
import { useAuth } from '@praxis/component-auth'
import {
  adminADGroup,
  producerADGroup,
  retouchingLeadADGroup,
  retouchingQAADGroup,
} from '../../constants/common'
import CopyToClipboard from 'react-copy-to-clipboard'

const WorkflowFlyoutContent = (props = {}) => {
  const {
    reusedSelection = [],
    imageWorkflow = {},
    handleImageStatusChange = () => {},
    MenuProps = {},
    imageStatusList = [],
    setImageWorkflow = () => {},
    assigneeInputValue = '',
    setAssigneeInputValue = () => {},
    qaAssigneeInputValue = '',
    setQaAssigneeInputValue = () => {},
    qaAsigneeList = [],
    isLoading = false,
    filteredReusedSelection = [],
    changeWorkflowStatus = () => {},
    unapprovedImages,
    tabWiseSelection,
    classes = {},
    asigneeList = [],
    notesFor3D = '',
    setNotesFor3D = () => {},
    sendToPipeline = () => {},
    approvedImages,
    failedFiles = [],
  } = props || {}

  const { image_status = '', assignee = '', qa_assignee = '' } = imageWorkflow
  const auth = useAuth()
  const { session: { userInfo: { memberOf = [] } = {} } = {} } = auth
  const isAdmin = memberOf?.includes(adminADGroup.toUpperCase())

  const getAsigneeList = (list) => {
    return list?.reduce((acc, user) => {
      const { display_name } = user
      return [...acc, { label: display_name, ...user }]
    }, [])
  }

  const reusedWarning = (size = 9) => {
    return reusedSelection?.length ? (
      <Grid item xs={12} lg={size} md={size}>
        {filteredReusedSelection?.length ? (
          <Alert severity="warning">
            Any changes made to the Multi TCIN image will be applied to all
            related reused images.
          </Alert>
        ) : (
          <Alert severity="warning">
            Please select some original images to do operations.
          </Alert>
        )}
      </Grid>
    ) : (
      <></>
    )
  }
  const renderFailureList = () => {
    const failedFilesList = failedFiles?.join(', ') || ''
    return (
      <div style={{ marginTop: '34px' }}>
        <Grid container spacing={2} alignItems={'end'}>
          <Grid item>
            <ErrorOutlineIcon className={classes.errorOutlineIconCls} />
          </Grid>
          <Grid item>
            <Typography
              color="error"
              component={'subtitle2'}
              sx={{ fontWeight: 'bold', color: '#B85300' }}
            >
              Failed TCINs: {failedFilesList}
            </Typography>
          </Grid>
          <Grid>
            <CopyToClipboard text={failedFilesList}>
              <Button
                className={classes.copyButtonCls}
                startIcon={<ContentCopyIcon />}
              >
                Copy To Clipboard
              </Button>
            </CopyToClipboard>
          </Grid>
        </Grid>
      </div>
    )
  }

  const isButtonDisabled = () => {
    if (approvedImages > 0) return true
    if (isLoading) return true
    if (isDeepEmpty(imageWorkflow)) return true
    if (!filteredReusedSelection?.length) return true
    if (image_status[0] === '3d Rework' && notesFor3D === '') return true
    return false
  }

  return (
    <>
      <Divider className={classes.drawerHeaderDivider} />
      {reusedWarning()}
      {!isLoading && failedFiles?.length > 0 && renderFailureList()}
      <Grid container className={classes.workflowDrawerContainer}>
        <Grid item xs={9} lg={9} md={9}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="image-status-label">
              Change Image Status To
            </InputLabel>
            <Select
              labelId="image-status-label"
              value={image_status}
              onChange={(event) => {
                handleImageStatusChange(event)
              }}
              input={<OutlinedInput label="Change Image Status To" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected || []).map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {(imageStatusList || [])
                ?.filter((data) => data !== 'Sent to Pipeline')
                ?.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {intersectionBy(
        memberOf,
        [producerADGroup, retouchingLeadADGroup, adminADGroup],
        toUpper
      )?.length > 0 ? (
        <Grid container className={classes.workflowDrawerContainer}>
          <Grid item xs={9} lg={9} md={9}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                value={assignee}
                onChange={(event, newValue) => {
                  setImageWorkflow((prev) => ({
                    ...prev,
                    assignee: newValue ? newValue : '',
                  }))
                }}
                getOptionLabel={(option) => option?.label || ''}
                inputValue={assigneeInputValue}
                onInputChange={(event, newInputValue) => {
                  setAssigneeInputValue(newInputValue)
                }}
                id="assignee-list"
                options={[
                  {
                    label: 'Remove Assignee',
                    display_name: 'Remove Assignee',
                  },
                  ...getAsigneeList(asigneeList),
                ]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Change Assignee To"
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
      {intersectionBy(
        memberOf,
        [retouchingLeadADGroup, adminADGroup, retouchingQAADGroup],
        toUpper
      )?.length > 0 ? (
        <Grid container className={classes.workflowDrawerContainer}>
          <Grid item xs={9} lg={9} md={9}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                value={qa_assignee}
                onChange={(event, newValue) => {
                  setImageWorkflow((prev) => ({
                    ...prev,
                    qa_assignee: newValue ? newValue : '',
                  }))
                }}
                getOptionLabel={(option) => option?.label || ''}
                inputValue={qaAssigneeInputValue}
                onInputChange={(event, newInputValue) => {
                  setQaAssigneeInputValue(newInputValue)
                }}
                id="assignee-list"
                options={[
                  {
                    label: 'Remove QA Assignee',
                    display_name: 'Remove QA Assignee',
                  },
                  ...getAsigneeList(qaAsigneeList),
                ]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Change QA Assignee To"
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
      {image_status[0] === '3d Rework' ? (
        <Grid container className={classes.workflowDrawerContainer}>
          <Grid item xs={9} lg={9} md={9}>
            <FormControl variant="outlined" fullWidth>
              <TextField
                variant="outlined"
                label="Notes to 3D"
                value={notesFor3D}
                disabled={false}
                onChange={(e) => {
                  setNotesFor3D(e.target.value)
                }}
                name="tagName"
              />
            </FormControl>
          </Grid>
        </Grid>
      ) : (
        ''
      )}
      <Grid container>
        <Grid item xs={9} lg={9} md={9}>
          <Stack direction="row" className={classes.mwButtonGroup} spacing={2}>
            <Button
              variant="outlined"
              className={classes.stausButton}
              onClick={() => {
                setImageWorkflow((prev) => ({
                  ...prev,
                  image_status: '',
                  assignee: '',
                  qa_assignee: '',
                }))
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={isButtonDisabled()}
              onClick={changeWorkflowStatus}
              className={`${classes.stausButton} ${classes.primaryButton}`}
            >
              {isLoading ? <CircularProgress size={14} /> : 'Apply'}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {imageStatusList.includes('Sent to Pipeline') ? (
        <>
          {' '}
          <Divider />
          <Grid container direction="column">
            <Grid>
              <Typography classes={{ root: classes.dialogMessage1 }}>
                {unapprovedImages
                  ? `${tabWiseSelection?.length} images selected, ${unapprovedImages} not approved`
                  : `${tabWiseSelection.length} approved images selected `}
              </Typography>
              <Typography variant="subtitle2">
                <p>
                  <em>
                    {`(
                      Workflow status updates are restricted for images in
                    'Approved', 'Waiting for Pipeline', 'Accepted by Pipeline',
                    or 'Rejected by Pipeline'.`}
                    {approvedImages > 0 && (
                      <span style={{ color: '#ed6c02' }}>
                        &nbsp;Please remove these images to proceed with the
                        status update.
                      </span>
                    )}
                    {`)`}
                  </em>
                </p>
              </Typography>
            </Grid>
            {isAdmin && (
              <Grid style={{ margin: '24px 0' }}>
                <Button
                  variant="contained"
                  disabled={
                    unapprovedImages ||
                    isLoading ||
                    !filteredReusedSelection?.length
                  }
                  onClick={sendToPipeline}
                  className={`${classes.primaryButton} ${classes.pipeLineButton}`}
                >
                  {isLoading ? (
                    <CircularProgress size={14} />
                  ) : (
                    'SEND TO PIPELINE'
                  )}
                </Button>
              </Grid>
            )}
          </Grid>
        </>
      ) : (
        <></>
      )}
    </>
  )
}

export default WorkflowFlyoutContent
